/**
 * Checks tab for the Full response reader (spec 29 §0.6.4).
 *
 * The whole-document view of the AI review: every open issue across the
 * response, with the four routes out of one — Fix it in the editor, hand it to
 * a person as a comment, dismiss it, or (when only a human can answer) send
 * the reader to the submission pack.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { CriteriaDrawer } from '@/components/drafting/CriteriaDrawer'
import { NewReviewForm } from '@/components/drafting/NewReviewForm'
import { ReviewRunNotice } from '@/components/drafting/ReviewRunNotice'
import { reviewRunNotice } from '@/lib/review-run-notice'
import { useReviewStore } from '@/stores/review-store'
import { useCommentsStore } from '@/stores/comments-store'
import { keepCriteriaForScope, REVIEW_SEVERITY_LABEL, type MergedReviewFinding } from '@/lib/schemas'
import type { BidArtifact, FullResponseSection as Section } from '@/lib/api'
import type { SectionAssignment } from '@/stores/section-assignments-store'
import { cn } from '@/lib/utils'
import { canEditSection } from './provenance'
import { useBidReadOnly } from '@/hooks/useViewOnly'

const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 }

function severityDotClass(severity: string): string {
  if (severity === 'high') return 'bg-destructive'
  if (severity === 'medium') return 'bg-[hsl(var(--status-warning))]'
  return 'bg-muted-foreground'
}

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, ' ')
}

function statusChipFor(finding: MergedReviewFinding) {
  switch (finding.actionStatus) {
    case 'addressing':
      return <Badge variant="outline" className="h-5 text-[10px]">Fixing…</Badge>
    case 'applied':
      return <Badge variant="success" className="h-5 text-[10px]">Fixed</Badge>
    case 'failed':
      return <Badge variant="destructive" className="h-5 text-[10px]">Fix failed</Badge>
    case 'commented':
      return <Badge variant="outline" className="h-5 text-[10px]">Commented</Badge>
    default:
      return null
  }
}

export interface FullResponseChecksTabProps {
  bidId: string
  artifactId: string
  artifactStatus: BidArtifact['status'] | undefined
  isOwner: boolean
  sections: Section[]
  assignmentByKey: Map<string, SectionAssignment>
  currentUserId: string | undefined
  onJump: (sectionKey: string, excerpt: string | null) => void
}

export function FullResponseChecksTab({
  bidId,
  artifactId,
  artifactStatus,
  isOwner,
  sections,
  assignmentByKey,
  currentUserId,
  onJump,
}: FullResponseChecksTabProps) {
  const navigate = useNavigate()
  // A read-only user (an org viewer, or a member who is not on the bid) gets
  // no Fix: it opens the editor, which would be read-only for them.
  const { isReadOnly } = useBidReadOnly(bidId)

  const activeFindings = useReviewStore((s) => s.activeFindings)
  const loadCriteria = useReviewStore((s) => s.loadCriteria)
  const criteria = useReviewStore((s) => s.criteria)
  const selectedCriteriaIds = useReviewStore((s) => s.selectedCriteriaIds)
  const clearSelectedCriteria = useReviewStore((s) => s.clearSelectedCriteria)
  const startReview = useReviewStore((s) => s.startReview)
  const activeRunStatus = useReviewStore((s) => s.activeRunStatus)
  const latestCheck = useReviewStore((s) => s.latestCheck)
  const loadLatestCheck = useReviewStore((s) => s.loadLatestCheck)

  // The latest check is the whole-response run the page follows. If it could
  // not check a criterion, say so — "No open issues" would be a false clean.
  const runNotice = reviewRunNotice(latestCheck?.run)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)

  // The reader page owns the findings load (it needs them for the margin
  // markers before this tab ever mounts); this tab only needs the criteria.
  useEffect(() => {
    void loadCriteria()
  }, [loadCriteria])

  const sectionOrder = useMemo(
    () => new Map(sections.map((s, i) => [s.sectionKey, i])),
    [sections],
  )

  const ordered = useMemo(() => {
    if (!activeFindings) return null
    return [...activeFindings].sort((a, b) => {
      const sev = (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3)
      if (sev !== 0) return sev
      const ai = a.sectionId ? sectionOrder.get(a.sectionId) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER
      const bi = b.sectionId ? sectionOrder.get(b.sectionId) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER
      return ai - bi
    })
  }, [activeFindings, sectionOrder])

  const isRunning = activeRunStatus === 'running'

  const handleRun = useCallback(async () => {
    if (selectedCriteriaIds.length === 0) return
    const runId = await startReview({
      bidId,
      artifactId,
      sectionKey: null,
      criteriaIds: selectedCriteriaIds,
    })
    if (runId) {
      setShowNewForm(false)
      clearSelectedCriteria()
      // The page polls only while the latest check reads "running"; without
      // this refresh it never sees a run started from this tab finish.
      await loadLatestCheck(artifactId)
    }
  }, [bidId, artifactId, selectedCriteriaIds, startReview, clearSelectedCriteria, loadLatestCheck])

  const known = useMemo(
    () => [...(criteria?.system ?? []), ...(criteria?.mine ?? [])],
    [criteria],
  )
  const canRun =
    keepCriteriaForScope(selectedCriteriaIds, known, 'artifact').length > 0 && !isRunning

  const handleJump = useCallback(
    (finding: MergedReviewFinding) => {
      if (finding.sectionId) {
        onJump(finding.sectionId, finding.excerpt ?? null)
        return
      }
      // A whole-response finding still quotes real prose — land on the section
      // that actually contains it rather than dumping the reader at the top.
      const excerpt = finding.excerpt
      const match = excerpt
        ? sections.find((s) => stripTags(s.html).toLowerCase().includes(excerpt.toLowerCase()))
        : undefined
      const target = match ?? sections[0]
      if (target) onJump(target.sectionKey, match ? excerpt : null)
    },
    [onJump, sections],
  )

  if (showNewForm) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-3 scrollbar-thin">
          <NewReviewForm
            onCancel={() => setShowNewForm(false)}
            onRun={() => void handleRun()}
            canRun={canRun}
            outlineHasSections={sections.length > 0}
            onManageCriteria={() => setDrawerOpen(true)}
            selectedCount={selectedCriteriaIds.length}
            isRunning={isRunning}
            canToggleScope={false}
            scope="artifact"
            sectionKey=""
            sectionTitle={null}
            scopeNotice={null}
            onScopeChange={() => {}}
          />
        </div>
        <CriteriaDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between px-3 pt-2">
        <p className="text-meta">Issues · {ordered?.length ?? 0}</p>
        <Button variant="ghost" size="sm" onClick={() => setShowNewForm(true)}>
          + New check
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 scrollbar-thin">
        {ordered === null ? (
          <div className="flex flex-col items-center justify-center py-10 text-center text-meta">
            <Loader2 className="mb-2 h-5 w-5 animate-spin" />
            Loading checks…
          </div>
        ) : ordered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            {runNotice ? (
              <div className="mb-3 w-full">
                <ReviewRunNotice message={runNotice} />
              </div>
            ) : (
              <p className="mb-3 text-meta">No open issues.</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => setShowNewForm(true)}>
              Check now
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {runNotice && <ReviewRunNotice message={runNotice} />}
            {ordered.map((finding) => (
              <IssueCard
                key={finding.issueKey}
                bidId={bidId}
                artifactId={artifactId}
                finding={finding}
                canFix={
                  finding.sectionId !== null &&
                  canEditSection({
                    artifactStatus,
                    isOwner,
                    assignment: assignmentByKey.get(finding.sectionId),
                    currentUserId,
                    isReadOnly,
                  })
                }
                assignment={finding.sectionId ? assignmentByKey.get(finding.sectionId) : undefined}
                currentUserId={currentUserId}
                onJump={() => handleJump(finding)}
                onFix={() =>
                  void navigate(
                    `/app/bids/${bidId}/responses/${artifactId}/sections/${finding.sectionId ?? ''}?issue=${finding.issueKey}`,
                  )
                }
              />
            ))}
          </div>
        )}
      </div>

      <CriteriaDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  )
}

// ===================
// One issue
// ===================

function IssueCard({
  bidId,
  artifactId,
  finding,
  canFix,
  assignment,
  currentUserId,
  onJump,
  onFix,
}: {
  bidId: string
  artifactId: string
  finding: MergedReviewFinding
  canFix: boolean
  assignment: SectionAssignment | undefined
  currentUserId: string | undefined
  onJump: () => void
  onFix: () => void
}) {
  const dismissFinding = useReviewStore((s) => s.dismissFinding)
  const handleActionUpdate = useReviewStore((s) => s.handleActionUpdate)
  const promoteFromFinding = useCommentsStore((s) => s.promoteFromFinding)

  const [dismissing, setDismissing] = useState(false)
  const [reason, setReason] = useState('')

  const routedTo =
    assignment?.userId && assignment.userId !== currentUserId
      ? assignment.fullName ?? assignment.email
      : null

  const handleComment = async () => {
    if (!finding.sectionId) return
    try {
      await promoteFromFinding(bidId, artifactId, {
        sectionId: finding.sectionId,
        startPos: null,
        endPos: null,
        quotedText: finding.excerpt ?? null,
        body: finding.message,
        sourceFindingRef: {
          issueKey: finding.issueKey,
          runId: finding.runId,
          findingIndex: finding.findingIndex,
          criterion: finding.criterionName,
          severity: finding.severity,
        },
      })
      handleActionUpdate({ issueKey: finding.issueKey, status: 'commented' })
    } catch {
      // Non-fatal — leave the button available for a retry.
    }
  }

  return (
    <div className="space-y-2 rounded-xl border bg-card p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="text-meta inline-flex min-w-0 items-center gap-1.5">
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', severityDotClass(finding.severity))}
          />
          <span className="truncate">
            {REVIEW_SEVERITY_LABEL[finding.severity]} · {finding.criterionName}
          </span>
        </span>
        {statusChipFor(finding)}
      </div>

      <button type="button" onClick={onJump} className="block w-full space-y-2 text-left">
        <p className="text-xs leading-relaxed">{finding.message}</p>
        {finding.excerpt && (
          <blockquote className="border-l-2 pl-2 text-[11px] italic text-muted-foreground">
            {finding.excerpt}
          </blockquote>
        )}
      </button>

      {routedTo && <p className="text-meta">Section by {routedTo}</p>}

      {dismissing ? (
        <div className="space-y-2">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why? (optional)"
            className="h-8 text-xs"
          />
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void dismissFinding(artifactId, finding.issueKey, reason || undefined)
              }}
            >
              Dismiss issue
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDismissing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {canFix && (
            <Button variant="ghost" size="sm" onClick={onFix}>
              Fix
            </Button>
          )}
          {finding.sectionId && finding.actionStatus !== 'commented' && (
            <Button variant="ghost" size="sm" onClick={() => void handleComment()}>
              Comment
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setDismissing(true)}>
            Dismiss
          </Button>
          {finding.actionClass === 'human_input' && (
            <Link
              to={`/app/bids/${bidId}/submit`}
              className="inline-flex items-center gap-0.5 text-meta text-[hsl(var(--brand))]"
            >
              Waiting on the team
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
